import Api from '../Api';
import { CompanyDataInSearch } from '../../components/authentication/registration-suggest/types/BusinessSuggest';
import { getConfiguration } from '../../helpers/Configuration';
import { getStore } from '../../helpers/env';
import { portalId } from '../../helpers/portal';
import { getFromLocalStorage, LS_KEYS, removeFromLocalStorage } from '../../helpers/window';
import { LoginResponse } from '../../store/sagas/authSaga';

interface AccountData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  dateOfBirth?: string;
  isCompany: string;
  company: string;
  kvkNumber: string;
  vatId: string;
  streetName: string;
  streetNumber: string;
  additionalStreetInfo?: string;
  postalCode: string;
  city: string;
  country: string;
  isSubscribed: boolean;
  isApiUser?: boolean;
  branchId?: string;
}

/**
 * Lowercase and remove spaces in the email
 * @param {string} email - The email address to format
 */
const formatEmail = (email: string) => email.toLowerCase().replace(/\s/g, '');

class AuthService extends Api {
  async login(params: {
    email: string;
    password: string;
    anonymousCartId?: string;
  }): Promise<LoginResponse> {
    const temporaryCustomerId = getFromLocalStorage(LS_KEYS.TEMPORARY_CUSTOMER_ID);
    const assetManagementTenant = process.env.GATSBY_ASSET_MANAGEMENT_TENANT;

    const { data } = await this.httpClient
      .post<LoginResponse>('/login', {
        email: params.email,
        portalId,
        password: params.password,
        store: process.env.GATSBY_STORE,
        ...params.anonymousCartId && { anonymousCartId: params.anonymousCartId },
        ...assetManagementTenant && { tenant: assetManagementTenant },
        ...temporaryCustomerId && { temporaryCustomerId },
      }, { withCredentials: true })
      .catch(this.errorHandler);

    if (data?.movedAssets?.status === 'completed') {
      removeFromLocalStorage(LS_KEYS.TEMPORARY_CUSTOMER_ID);
    }

    return Promise.resolve(data);
  }

  async registerFull(accountData: AccountData, storeKey: string, locale: string) {
    const {
      email, dateOfBirth, branchId, ...restData
    } = accountData;
    const trimmedBranchId = branchId?.trim();
    const temporaryCustomerId = getFromLocalStorage(LS_KEYS.TEMPORARY_CUSTOMER_ID);
    const assetManagementTenant = process.env.GATSBY_ASSET_MANAGEMENT_TENANT;

    const { data } = await this.httpClient
      .post('/register', {
        email: formatEmail(email),
        ...dateOfBirth && { dateOfBirth },
        ...restData,
        ...trimmedBranchId && { branchId: trimmedBranchId },
        ...assetManagementTenant && { tenant: assetManagementTenant },
        ...temporaryCustomerId && { temporaryCustomerId },
        locale: locale.substring(0, 2),
        localeInCountry: locale,
        storeKey,
      }, { withCredentials: true })
      .catch(this.errorHandler);

    if (temporaryCustomerId && data?.movedAssets?.status === 'completed') {
      removeFromLocalStorage(temporaryCustomerId);
    }

    return Promise.resolve(data);
  }

  async businessSuggest(searchQuery: string) {
    const { data } = await this.httpClient
      .post('/business-suggest', {
        searchQuery,
        countryCode: getConfiguration().officeAddress.countryCode,
        store: getStore(),
      })
      .catch(this.errorHandler);
    return Promise.resolve(data);
  }

  async businessProfile(companyData: CompanyDataInSearch) {
    const { data } = await this.httpClient
      .post('/business-profile', {
        countryCode: getConfiguration().officeAddress.countryCode,
        ...companyData,
      })
      .catch(this.errorHandler);
    return Promise.resolve(data);
  }
}

export default AuthService;
